import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { isMoreThanYearAgo } from "@/helpers/dates.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  ApiReportingDetailsSortField,
  ApiReportingSalaryDetails,
} from "@/types/reporting/reporting.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  DropdownMenu,
  DropdownMenuItem,
  Stack,
  Table,
  TableSorting,
  Text,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CompensationRollupType } from "@/types/analytics/analytics.employee.types.ts";

interface EmployeeSalaryTableV2Props {
  data: ApiReportingSalaryDetails[];
  isLoading: boolean;
  sorting: TableSorting;
  onSortUpdated: (sorting: TableSorting) => void;
  rollupType: CompensationRollupType;
}

export function EmployeeSalaryTableV2({
  data,
  isLoading,
  sorting,
  onSortUpdated,
  rollupType,
}: EmployeeSalaryTableV2Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { renderCurrency, renderDate, renderTimeAgo } = useFormatter();

  const formatGrowth = (value: number | null) => (value === null ? "-" : `${value}%`);

  const columns = useMemo<ColumnDef<ApiReportingSalaryDetails>[]>(() => {
    const result: ColumnDef<ApiReportingSalaryDetails>[] = [
      {
        id: ApiReportingDetailsSortField.employee_name,
        header: t("core:employee"),
        cell: (cell) => (
          <EmployeeHoverCard
            employee={cell.row.original.employee}
            position={getEmployeePositionTitle(cell.row.original.employee)}
          >
            <EmployeeItem employee={cell.row.original.employee} enableLink showPosition />
          </EmployeeHoverCard>
        ),
        ...ColumnSizes.employee,
        enableSorting: true,
        sortDescFirst: false,
      },
      {
        id: "hire_date",
        header: t("core:hire_date"),
        cell: (cell) => (
          <Text className="text-secondary">
            {cell.row.original.employee.date_hired_on
              ? renderDate(new Date(cell.row.original.employee.date_hired_on))
              : "-"}
          </Text>
        ),
        ...ColumnSizes.date,
      },
      {
        id: ApiReportingDetailsSortField.salary_net,
        accessorKey: "salary_net",
        header: t("reporting:salary_net"),
        cell: (cell) => (
          <Stack>
            <Text>
              {cell.row.original.salary_net
                ? renderCurrency(
                    `${cell.row.original.salary_net.value}`,
                    cell.row.original.salary_net.currency
                  )
                : "-"}
            </Text>
            {cell.row.original.salary_net?.currency !==
              cell.row.original.salary_net_original?.currency && (
              <Text className="text-secondary">
                {cell.row.original.salary_net_original
                  ? renderCurrency(
                      `${cell.row.original.salary_net_original.value}`,
                      cell.row.original.salary_net_original.currency
                    )
                  : "-"}
              </Text>
            )}
          </Stack>
        ),
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      {
        id: ApiReportingDetailsSortField.salary_gross,
        accessorKey: "salary_gross",
        header: t("reporting:salary_gross"),
        cell: (cell) => (
          <Stack>
            <Text>
              {cell.row.original.salary_gross
                ? renderCurrency(
                    `${cell.row.original.salary_gross.value}`,
                    cell.row.original.salary_gross.currency
                  )
                : "-"}
            </Text>
            {cell.row.original.salary_gross?.currency !==
              cell.row.original.salary_gross_original?.currency && (
              <Text className="text-secondary">
                {cell.row.original.salary_gross_original
                  ? renderCurrency(
                      `${cell.row.original.salary_gross_original.value}`,
                      cell.row.original.salary_gross_original.currency
                    )
                  : "-"}
              </Text>
            )}
          </Stack>
        ),
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      {
        id: ApiReportingDetailsSortField.last_updated,
        accessorKey: "date_last_updated",
        header: t("reporting:last_updated"),
        cell: (cell) => {
          const date = cell.row.original.date_last_updated;
          const isOlderThanYear = date && isMoreThanYearAgo(date);
          return (
            <Text className={isOlderThanYear ? "text-warning" : "text-secondary"}>
              {date ? renderTimeAgo(new Date(date)) : "-"}
            </Text>
          );
        },
        ...ColumnSizes.date,
        enableSorting: true,
        sortDescFirst: false,
      },
      {
        id: ApiReportingDetailsSortField.period_growth,
        accessorKey: "period_growth",
        header: t("reporting:period_growth"),
        cell: (cell) => <Text>{formatGrowth(cell.row.original.period_growth)}</Text>,
        ...ColumnSizes.narrow,
        enableSorting: true,
        sortDescFirst: false,
      },
      {
        id: ApiReportingDetailsSortField.lifetime_growth,
        accessorKey: "lifetime_growth",
        header: t("reporting:lifetime_growth"),
        cell: (cell) => <Text>{formatGrowth(cell.row.original.lifetime_growth)}</Text>,
        ...ColumnSizes.narrow,
        enableSorting: true,
        sortDescFirst: false,
      },
    ];
    if (rollupType === CompensationRollupType.total_cost) {
      result.splice(
        4,
        0,
        {
          id: "salary_total_cost",
          header: t("reporting:metric_total_cost"),
          cell: ({ row }) => (
            <Text>
              {row.original.salary_total_cost
                ? renderCurrency(
                    String(row.original.salary_total_cost.value),
                    row.original.salary_total_cost.currency
                  )
                : "-"}
            </Text>
          ),
          ...ColumnSizes.standard,
        },
        {
          id: "employer_contributions",
          header: t("reporting:metric_employer_contributions"),
          cell: ({ row }) => (
            <Text>
              {row.original.employer_contributions
                ? renderCurrency(
                    String(row.original.employer_contributions.value),
                    row.original.employer_contributions.currency
                  )
                : "-"}
            </Text>
          ),
          ...ColumnSizes.standard,
        }
      );
    }
    return result;
  }, [renderCurrency, renderDate, renderTimeAgo, rollupType, t]);

  const renderRowAction = useCallback(
    (row: Row<ApiReportingSalaryDetails>) => (
      <DropdownMenu>
        <DropdownMenuItem
          title={t("core:view_profile")}
          onClick={() => navigate(`/employees/${row.original.employee.id}`)}
        />
        <DropdownMenuItem
          title={t("core:compensation")}
          onClick={() => navigate(`/employees/${row.original.employee.id}/work-info`)}
        />
      </DropdownMenu>
    ),
    [navigate, t]
  );

  return (
    <Table
      data={data}
      columns={columns}
      renderRowAction={renderRowAction}
      showSkeleton={isLoading}
      stickyHeader
      stickyFirstColumn
      sorting={sorting}
      onSortUpdated={onSortUpdated}
      emptyStateComponent={<EmptyStateNothingFound />}
    />
  );
}
